import { useState, useCallback, useEffect, useLayoutEffect, useMemo } from "react";

const useIsomorphicLayoutEffect = typeof window !== "undefined"
  ? useLayoutEffect
  : useEffect;

interface DeviceInfo {
  isMobile: boolean;
  isPc: boolean;
  isIos: boolean;
  isAndroid: boolean;
  isAndroidWebView: boolean;
  isIosWebView: boolean;
  isHybridApp: boolean;
}

export function useCheckDevice(initialDeviceInfo?: Partial<DeviceInfo>) {
  /**
   * 서버사이드 header에서 user-agent 정보를 가공하여 넣어주면 
   * 해당 데이터로 설정할 수 있도록 수정
   */
  const [match, setMatch] = useState<MediaQueryList | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: initialDeviceInfo?.isMobile ?? false,
    isPc: false,
    isIos: initialDeviceInfo?.isIos ?? false,
    isAndroid: initialDeviceInfo?.isAndroid ?? false,
    isAndroidWebView: initialDeviceInfo?.isAndroidWebView ?? false,
    isIosWebView: initialDeviceInfo?.isIosWebView ?? false,
    isHybridApp: (initialDeviceInfo?.isAndroidWebView || initialDeviceInfo?.isIosWebView) ?? false,
  });

  useIsomorphicLayoutEffect(() => {
    setMatch(window.matchMedia("(max-width: 768px)"));
  }, []);

  const handleMatch = useCallback((e: MediaQueryList | MediaQueryListEvent) => {
    setDeviceInfo(prev => ({
      ...prev,
      isMobile: e.matches,
      isPc: !e.matches,
    }));
  }, []);

  useIsomorphicLayoutEffect(() => {
    match?.addEventListener("change", handleMatch);

    if (match) {
      handleMatch(match);
    }

    return () => {
      match?.removeEventListener("change", handleMatch);
    }
  }, [handleMatch, match]);

  const [userAgent, setUserAgent] = useState<string>("");

  useIsomorphicLayoutEffect(() => {
    setUserAgent(navigator.userAgent);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (userAgent?.match(/AndroidWebView/i)) {
      setDeviceInfo(prev => ({
        ...prev,
        isAndroidWebView: true,
        isIosWebView: false,
        isHybridApp: true,
      }));
    }
    else if (userAgent?.match(/IOSWebView/i)) {
      setDeviceInfo(prev => ({
        ...prev,
        isAndroidWebView: false,
        isIosWebView: true,
        isHybridApp: true,
      }));
    }
    else if (userAgent?.match(/Android/i)) {
      setDeviceInfo(prev => ({
        ...prev,
        isIos: false,
        isAndroid: true,
      }));
    } else if (userAgent?.match(/iPhone|iPad|iPod/i)) {
      setDeviceInfo(prev => ({
        ...prev,
        isIos: true,
        isAndroid: false,
      }));
    } else {
      setDeviceInfo(prev => ({
        ...prev,
        isIos: false,
        isAndroid: false,
      }));
    }
  }, [userAgent]);

  return useMemo(() => deviceInfo, [deviceInfo]);
}
